import { Carousel } from '@intigral/domain/lib/core/entities/ScreenEntities';
import {
    CatchUpCarousel,
    Providers,
    ProvidersGrid,
    RewindCarousel,
    SearchResults,
    TvGuideCarousel,
    WatchlistCarousel,
} from '../components/ui';
import { BSAddonsBanner } from '../components/ui/BSAddonsBanner';
import { ChannelsCarousel } from '../components/ui/ChannelsCarousel';
import GamesCarousel from '../components/ui/GamesCarousel';
import { GenresCarousel } from '../components/ui/GenresCarousel';
import { ImagesCarousel } from '../components/ui/ImagesCarousel';
import MarketingLanding from '../components/ui/MarketingLanding';
import { MoodsRecommendation } from '../components/ui/MoodsRecommendation';
import { MpxFeedCarousel } from '../components/ui/MpxFeedCarousel';
import { RemindersCarousel } from '../components/ui/RemindersCarousel';
import { StreamingCarousel } from '../components/ui/StreamingCarousel';
import { TaFeedCarousel } from '../components/ui/TaFeedCarousel';
import { TVodCarousel } from '../components/ui/TVodCarousel';
import { UpgradedBanner } from '../components/ui/UpgradedBanner';
import { UpgradedBannerFail } from '../components/ui/UpgradedBannerFail';
import { UserWatchlist } from '../components/ui/UserWatchlist';
import { WatchAgainCarousel } from '../components/ui/WatchAgainCarousel';
import { isGamificationEnabled } from '../features/gamification/utils/GamificationUtils';
import { __ } from '../translations';
import { getUserInfo } from '../utils/user';
import {
    AdBanner,
    AddonCarousel,
    Banner,
    BundleAndSaveHeader,
    ContinueWatch,
    HorizontalBanner,
    LongVertical,
    MainBanner,
    MlpBanner,
    PickAndChooseHeader,
    PPVCarousel,
} from '../features';
import { isGuestUser, isGuestVisibleCarousel } from '../config/guestMode';

type ComponentMapperProps = {
    carousel: Carousel;
    data: any;
    screenId?: null | string;
    carouselIndex?: null | number;
    isLoading?: boolean;
};

export const ComponentMapper: React.FC<ComponentMapperProps> = ({
    carousel,
    data,
    screenId = null,
    carouselIndex = null,
    isLoading,
}) => {
    const componentType = carousel?.wcpType || carousel?.type;
    const user = getUserInfo();
    if (isGuestUser(user) && !isGuestVisibleCarousel(carousel)) {
        return null;
    }
    switch (componentType) {
        case 'HORIZONTAL_OUTSTREAM_AD':
            return <AdBanner carousel={carousel as any} name="HORIZONTAL_OUTSTREAM_AD" />;
        case 'HORIZONTAL_MLP_BANNER':
            return <MlpBanner carousel={carousel} name="HORIZONTAL_MLP_BANNER" />;
        case 'REWARD_BANNER':
            return isGamificationEnabled()?.enable && <Banner carousel={carousel} />;
        case 'HERO':
            return <MainBanner carousel={carousel} name="HERO" />;
        case 'VERTICAL_PROMOTION':
            return <LongVertical carousel={carousel} name="VERTICAL_PROMOTION" />;
        case 'PROMOTION_BANNER':
        case 'HORIZONTAL_PROMOTIONS':
            return <HorizontalBanner carousel={carousel} />;
        case 'TVOD_PORTRAIT':
            return <TVodCarousel carousel={carousel} data={data} />;
        case 'MPX_PORTRAIT':
            return <MpxFeedCarousel carousel={carousel} />;
        case 'TA_PORTRAIT':
        case 'WATCH_NEXT_LANDSCAPE':
        case 'TOP_TEN_PORTRAIT':
        case 'LANDSCAPE_LIVETV_TRENDING':
            return <TaFeedCarousel carousel={carousel} />;
        case 'WATCH_AGAIN_PORTRAIT':
            return <WatchAgainCarousel carousel={carousel} />;
        case 'MINI_EPG':
            return (
                <TvGuideCarousel
                    meta={{
                        data_stream_type: 'user_mini_epg',
                        title: { en: __(carousel?.title_resource_key), ar: __(carousel?.title_resource_key) },
                    }}
                    params={{ channels_count: 4, refresh_seconds: 30 }}
                    name={carousel?.id}
                    data_stream={'jawwy_user_mini_epg'}
                    carousel={carousel}
                />
            );
        case 'PROVIDERS':
            return (
                <Providers
                    content={data}
                    meta={{
                        title: {
                            en: __(carousel?.title_resource_key),
                            ar: __(carousel?.title_resource_key),
                        },
                    }}
                    carousel={carousel}
                />
            );
        case 'PROVIDER_GRID':
        case 'PROVIDERS_GRID':
            return (
                <ProvidersGrid
                    meta={{
                        title: {
                            en: __(carousel?.title_resource_key),
                            ar: __(carousel?.title_resource_key),
                        },
                    }}
                    carousel={carousel}
                />
            );
        case 'REWIND_LANDSCAPE':
            return (
                <RewindCarousel
                    content={data ? data : []}
                    meta={{
                        title: {
                            en: __(carousel?.title_resource_key),
                            ar: __(carousel?.title_resource_key),
                        },
                    }}
                    name={'Rewind Carousel'}
                />
            );
        case 'SEARCH_RESULTS':
            return <SearchResults />;
        case 'CHANNEL_HISTORY':
            return (
                <ChannelsCarousel
                    content={data}
                    meta={{
                        title: {
                            en: __(carousel?.title_resource_key),
                            ar: __(carousel?.title_resource_key),
                        },
                    }}
                    // @ts-expect-error Property 'filter_params' does not exist
                    params={{ items_count: 8, filter_params: null }}
                    carousel={carousel}
                />
            );
        case 'CHANNEL_LISTING':
            return (
                <ChannelsCarousel
                    content={Object.keys(data || {}).map((key) => data[key])}
                    meta={{
                        title: {
                            en: __(carousel?.title_resource_key),
                            ar: __(carousel?.title_resource_key),
                        },
                    }}
                    // @ts-expect-error Property 'filter_params' does not exist
                    params={{ items_count: 12, filter_params: null }}
                    carousel={carousel}
                />
            );
        case 'CONTENT_BY_GENRE':
            return (
                <GenresCarousel
                    data={data}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                        listbtn_target: 'movies',
                        subscriptionGuid: 'DISCOVERY_PLUS_SUBSCRIPTION',
                        subscriptionGuids: 'false',
                        carousel_link_target: 'movies',
                        carouselType: 'vod',
                    }}
                    carousel={carousel}
                />
            );

        case 'CHANNEL_BY_CATEGORIES':
            return (
                <GenresCarousel
                    data={data}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                        listbtn_target: 'movies',
                        subscriptionGuid: 'DISCOVERY_PLUS_SUBSCRIPTION',
                        subscriptionGuids: 'false',
                        carousel_link_target: 'movies',
                        carouselType: 'livetv',
                    }}
                    carousel={carousel}
                />
            );
        case 'WATCHLIST_LANDSCAPE':
            return (
                <WatchlistCarousel
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                    name={'watchlist'}
                    carousel={carousel}
                />
            );
        case 'WATCHLIST_MASONRY':
            return (
                <UserWatchlist
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                    name={'watchlist'}
                    carousel={carousel}
                    data={data}
                />
            );
        case 'CHANNEL_INFO':
            return (
                <StreamingCarousel
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                        empty_msg: 'false',
                        is_rented: false,
                        programType: 'all',
                        hasMultiview: 'false',
                        seeAllScreen: 'false',
                        defaultActive: 'carousel-view',
                        listbtn_target: 'movies',
                        template_params: {
                            menu_type: 'list',
                        },
                        data_stream_type: 'vod_list',
                        subscriptionGuid: 'DISCOVERY_PLUS_SUBSCRIPTION',
                        subscriptionGuids: 'true',
                        carousel_link_target: 'movies',
                        show_title_on_listview: false,
                    }}
                    data={data}
                    carousel={carousel}
                />
            );
        case 'CHANNEL_LISTING_BY_ID':
            return (
                <StreamingCarousel
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                    data={data}
                    carousel={carousel}
                />
            );
        case 'MPX_BIOGRAPHY':
            return (
                <ImagesCarousel
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                    carousel={carousel}
                />
            );
        case 'VOD_REMINDERS':
            return (
                <RemindersCarousel
                    data={data}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                        reminder: {
                            type: 'VOD',
                        },
                        empty_msg: 'false',
                        is_rented: false,
                        programType: 'all',
                        hasMultiview: 'false',
                        seeAllScreen: 'false',
                        defaultActive: 'carousel-view',
                        listbtn_target: 'movies',
                        template_params: {
                            menu_type: 'list',
                        },
                        data_stream_type: 'vod_list',
                        subscriptionGuid: 'DISCOVERY_PLUS_SUBSCRIPTION',
                        subscriptionGuids: 'true',
                        carousel_link_target: 'movies',
                        show_title_on_listview: false,
                    }}
                    carousel={carousel}
                />
            );

        case 'LINEAR_REMINDERS':
            return (
                <RemindersCarousel
                    data={data}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                        reminder: {
                            type: 'LINEAR',
                        },
                        empty_msg: 'false',
                        is_rented: false,
                        programType: 'all',
                        hasMultiview: 'false',
                        seeAllScreen: 'false',
                        defaultActive: 'carousel-view',
                        listbtn_target: 'movies',
                        template_params: {
                            menu_type: 'list',
                        },
                        data_stream_type: 'vod_list',
                        subscriptionGuid: 'DISCOVERY_PLUS_SUBSCRIPTION',
                        subscriptionGuids: 'true',
                        carousel_link_target: 'movies',
                        show_title_on_listview: false,
                    }}
                    carousel={carousel}
                />
            );
        case 'PPV_REMINDERS':
            return (
                <RemindersCarousel
                    data={data}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                        reminder: {
                            type: 'PPV',
                        },
                        empty_msg: 'false',
                        is_rented: false,
                        programType: 'all',
                        hasMultiview: 'false',
                        seeAllScreen: 'false',
                        defaultActive: 'carousel-view',
                        template_params: {
                            menu_type: 'list',
                        },
                        show_title_on_listview: false,
                    }}
                    carousel={carousel}
                />
            );
        case 'PORTRAIT_ADDON_WITH_STATIC_BANNER':
            return (
                <AddonCarousel
                    content={carousel?.data?.contentV3 || carousel?.data?.contentV2 || carousel?.data?.content}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                    carousel={carousel}
                />
            );
        case 'PICK_AND_CHOOSE_CAROUSEL_HEADER':
            return (
                <PickAndChooseHeader
                    carousel={carousel}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                />
            );
        case 'BUNDLE_AND_SAVE_CAROUSEL_HEADER':
            return (
                <BundleAndSaveHeader
                    carousel={carousel}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                />
            );
        case 'PPV_PORTRAIT':
            return (
                <PPVCarousel
                    content={carousel?.data?.contentV2 ? carousel?.data?.contentV2 : carousel?.data?.content}
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                    carousel={carousel}
                />
            );
        case 'BUNDLE_AND_SAVE_BANNER_LANDSCAPE':
            return <BSAddonsBanner content={carousel?.data?.content} carousel={carousel} />;
        case 'UPGRADED_BANNER_LANDSCAPE':
            return <UpgradedBanner content={carousel?.data} />;
        case 'UPGRADED_BANNER_FAIL_LANDSCAPE':
            return <UpgradedBannerFail content={carousel?.data} />;
        case 'MOODS_RECOMMENDATION_MIX':
            return <MoodsRecommendation carousel={carousel} />;
        case 'CATCHUP':
        case 'CATCHUP_MIXED':
            return (
                <CatchUpCarousel
                    meta={{
                        title: { en: __(carousel?.title_resource_key), ar: __(carousel?.title_resource_key) },
                        title_resource_key: carousel?.title_resource_key,
                        dataSource: carousel?.dataSources?.[0] ?? '',
                        isMixedCatchup: componentType === 'CATCHUP_MIXED',
                    }}
                    carousel={carousel}
                />
            );
        case 'STC_PLAY_GAMES_LANDSCAPE':
            return (
                <GamesCarousel
                    meta={{
                        title: {
                            ar: __(carousel?.title_resource_key),
                            en: __(carousel?.title_resource_key),
                        },
                    }}
                    carousel={carousel}
                />
            );
        case 'CONTENT':
            return <MarketingLanding carousel={carousel} screenId={screenId} carouselIndex={carouselIndex} />;
        case 'CONTINUE_WATCHING_LANDSCAPE':
        case 'CONTINUE_WATCHING_TA_LANDSCAPE':
            return (
                <ContinueWatch
                    carousel={carousel}
                    meta={{
                        title: {
                            en: __(carousel?.title_resource_key),
                            ar: __(carousel?.title_resource_key),
                        },
                    }}
                />
            );
    }
};
